import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { ErrorBanner } from '@/components/ui';
import { templatesService, type SearchTemplate } from '@/api/services/templates';
import { useMe } from '@/context/MeContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { formatApiError } from '@/utils/formatApiError';
import { canInstallTemplates } from '@/utils/navCapabilities';

export default function TemplateInstallPage() {
  const [params] = useSearchParams();
  const templateId = params.get('id') || '';
  const navigate = useNavigate();
  const { me } = useMe();
  const { isPersonalMode, activeWorkspaceName } = useWorkspace();
  const canInstall = canInstallTemplates(me);
  const installTarget = isPersonalMode
    ? 'Personal'
    : activeWorkspaceName ?? 'Organization';
  const [template, setTemplate] = useState<SearchTemplate | null>(null);
  const [name, setName] = useState('');
  const [keywords, setKeywords] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!templateId || !canInstall) return;
    templatesService
      .get(templateId)
      .then((tpl) => {
        setTemplate(tpl);
        setName(tpl.title);
        setKeywords(String(tpl.preview?.keywords || tpl.preview?.filters?.search_text || ''));
      })
      .catch((e) => setError(formatApiError(e, 'Failed to load template')));
  }, [templateId, canInstall]);

  async function install() {
    if (!templateId || !name.trim()) return;
    setBusy(true);
    setError('');
    try {
      const overrides: Record<string, unknown> = {};
      if (keywords.trim()) overrides.keywords = keywords.trim();
      if (priceMax.trim()) overrides.price_max = Number(priceMax);
      const result = await templatesService.install({
        templateId,
        name: name.trim(),
        overrides: Object.keys(overrides).length ? overrides : undefined,
      });
      navigate(`/searches/${result.search.id}`);
    } catch (e) {
      setError(formatApiError(e, 'Install failed'));
    } finally {
      setBusy(false);
    }
  }

  if (!canInstall) {
    return (
      <>
        <PageHeader title="Install template" />
        <p className="muted">You do not have permission to install templates.</p>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Install template"
        subtitle={`Install into ${installTarget}`}
      >
        <Link to={templateId ? `/templates/${templateId}` : '/templates'} className="btn btn-ghost btn-sm">
          Back
        </Link>
      </PageHeader>

      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}

      {!template ? <p className="muted">Loading…</p> : (
        <div className="card card-elevated template-install-form">
          <p className="muted">Installing: {template.title} (v{template.version})</p>
          <label>
            Search name
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Keywords (optional override)
            <input className="input" value={keywords} onChange={(e) => setKeywords(e.target.value)} />
          </label>
          <label>
            Max price (optional)
            <input className="input" type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} />
          </label>
          <p className="muted">
            This creates a normal search. Future template updates will not change it automatically.
          </p>
          <div className="template-card__actions">
            <button type="button" className="btn" disabled={busy} onClick={() => void install()}>
              {busy ? 'Installing…' : `Install into ${installTarget}`}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
